import React from 'react';
import { X, Download, Share } from 'lucide-react';

const InstallModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  // Detect device/browser
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const isAndroid = /android/i.test(navigator.userAgent);
  const isChrome = /chrome/i.test(navigator.userAgent) && !/edge/i.test(navigator.userAgent);

  return (
    <div className="fixed inset-0 z-[100000] flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-zinc-900 border-2 border-white/10 rounded-t-3xl sm:rounded-3xl w-full sm:max-w-md mx-auto shadow-2xl max-h-[85vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-zinc-900 border-b border-white/5 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-heading tracking-wide text-white">
              Εγκατάσταση Εφαρμογής
            </h2>
            <p className="text-xs text-zinc-500 mt-1">DIMAKAS · Modern Male Image</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/5 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-zinc-400" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-6 space-y-6">
          {/* iOS Safari Instructions */}
          {isIOS && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <Share className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-white">iOS Safari</p>
                  <p className="text-xs text-zinc-400">Ακολουθήστε τα βήματα</p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#A9B2B9] flex items-center justify-center text-black font-bold text-sm">
                    1
                  </div>
                  <div>
                    <p className="text-sm text-white font-medium">Πατήστε το κουμπί "Share"</p>
                    <p className="text-xs text-zinc-400 mt-1">
                      Βρίσκεται στο κάτω μέρος της οθόνης (τετράγωνο με βέλος προς τα πάνω)
                    </p>
                    <div className="mt-2 p-3 bg-zinc-800 rounded-lg border border-white/5">
                      <div className="flex items-center justify-center gap-2 text-blue-400">
                        <Share className="w-8 h-8" />
                        <span className="text-xs">← Πατήστε αυτό</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#A9B2B9] flex items-center justify-center text-black font-bold text-sm">
                    2
                  </div>
                  <div>
                    <p className="text-sm text-white font-medium">Επιλέξτε "Add to Home Screen"</p>
                    <p className="text-xs text-zinc-400 mt-1">
                      Κάντε scroll στη λίστα επιλογών και βρείτε την επιλογή
                    </p>
                    <div className="mt-2 p-3 bg-zinc-800 rounded-lg border border-white/5 text-center">
                      <p className="text-sm text-white">📱 Add to Home Screen</p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#A9B2B9] flex items-center justify-center text-black font-bold text-sm">
                    3
                  </div>
                  <div>
                    <p className="text-sm text-white font-medium">Πατήστε "Add"</p>
                    <p className="text-xs text-zinc-400 mt-1">
                      Η εφαρμογή θα εμφανιστεί στην αρχική οθόνη!
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Android Chrome Instructions */}
          {isAndroid && isChrome && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-green-500/10 border border-green-500/20 rounded-xl">
                <div className="p-2 bg-green-500/20 rounded-lg">
                  <Download className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-white">Android Chrome</p>
                  <p className="text-xs text-zinc-400">Εύκολη εγκατάσταση</p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#A9B2B9] flex items-center justify-center text-black font-bold text-sm">
                    1
                  </div>
                  <div>
                    <p className="text-sm text-white font-medium">Πατήστε τα 3 τελείες (⋮)</p>
                    <p className="text-xs text-zinc-400 mt-1">
                      Πάνω δεξιά στον browser
                    </p>
                    <div className="mt-2 p-3 bg-zinc-800 rounded-lg border border-white/5 text-center">
                      <span className="text-3xl text-white">⋮</span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#A9B2B9] flex items-center justify-center text-black font-bold text-sm">
                    2
                  </div>
                  <div>
                    <p className="text-sm text-white font-medium">Επιλέξτε "Εγκατάσταση εφαρμογής" ή "Add to Home screen"</p>
                    <p className="text-xs text-zinc-400 mt-1">
                      Η εφαρμογή θα εγκατασταθεί αυτόματα!
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Desktop Chrome/Edge Instructions */}
          {!isIOS && !isAndroid && isChrome && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-purple-500/10 border border-purple-500/20 rounded-xl">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <Download className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-white">Desktop Browser</p>
                  <p className="text-xs text-zinc-400">Chrome ή Edge</p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#A9B2B9] flex items-center justify-center text-black font-bold text-sm">
                    1
                  </div>
                  <div>
                    <p className="text-sm text-white font-medium">Κοιτάξτε στη γραμμή διευθύνσεων</p>
                    <p className="text-xs text-zinc-400 mt-1">
                      Θα δείτε ένα εικονίδιο εγκατάστασης (⊕ ή εικονίδιο υπολογιστή)
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#A9B2B9] flex items-center justify-center text-black font-bold text-sm">
                    2
                  </div>
                  <div>
                    <p className="text-sm text-white font-medium">Πατήστε το εικονίδιο</p>
                    <p className="text-xs text-zinc-400 mt-1">
                      Ή: Μενού (⋮) → "Εγκατάσταση DIMAKAS"
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Generic Instructions (other browsers) */}
          {!isIOS && !isChrome && !isSafari && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-zinc-700/50 border border-white/10 rounded-xl">
                <div className="p-2 bg-white/10 rounded-lg">
                  <Download className="w-5 h-5 text-zinc-300" />
                </div>
                <div>
                  <p className="text-sm font-medium text-white">Άλλος Browser</p>
                  <p className="text-xs text-zinc-400">Γενικές οδηγίες</p>
                </div>
              </div>

              <p className="text-sm text-zinc-300">
                Για καλύτερη εμπειρία, χρησιμοποιήστε:
              </p>
              <ul className="space-y-2 text-sm text-zinc-400">
                <li className="flex items-center gap-2">
                  <span className="text-[#A9B2B9]">•</span>
                  Chrome (Android/Desktop)
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#A9B2B9]">•</span>
                  Safari (iOS)
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#A9B2B9]">•</span>
                  Edge (Desktop)
                </li>
              </ul>
            </div>
          )}

          {/* Benefits */}
          <div className="pt-4 border-t border-white/5">
            <p className="text-xs font-heading tracking-wide text-zinc-500 uppercase mb-3">
              Πλεονεκτήματα Εγκατάστασης
            </p>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-zinc-300">
                <span className="text-[#A9B2B9]">✓</span>
                Πρόσβαση με ένα tap από την αρχική οθόνη
              </div>
              <div className="flex items-center gap-2 text-sm text-zinc-300">
                <span className="text-[#A9B2B9]">✓</span>
                Λειτουργία σαν native εφαρμογή
              </div>
              <div className="flex items-center gap-2 text-sm text-zinc-300">
                <span className="text-[#A9B2B9]">✓</span>
                Ειδοποιήσεις για τα ραντεβού σας
              </div>
              <div className="flex items-center gap-2 text-sm text-zinc-300">
                <span className="text-[#A9B2B9]">✓</span>
                Γρηγορότερη απόδοση
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-zinc-900 border-t border-white/5 px-6 py-4">
          <button
            onClick={onClose}
            className="w-full py-3 bg-white/5 hover:bg-white/10 text-white rounded-xl transition-colors font-medium"
          >
            Κλείσιμο
          </button>
        </div>
      </div>
    </div>
  );
};

export default InstallModal;
